import { useSetState, useUpdateEffect } from 'ahooks'
import { Checkbox, Radio } from 'antd-mobile'
import { RightOutline } from 'antd-mobile-icons'
import classNames from 'classnames'
import { throttle, cloneDeep } from 'lodash-es'
import { observer } from 'mobx-react-lite'
import React, { useEffect, Fragment, useMemo } from 'react'

import { HSearchView, HTreeNode } from '@/components/NewEditForm/UILibrary/HYBVant'
import { SELECT_MODE } from '@/constants'
import { uniqueItems, flattenTree, isNotEmptyArray, findValuesByKeys } from '@/utils'

import { employeeSelectorApi, getemployeeApi } from '../services'

import './index.less'

interface PersonnelSelectViewProps {
  mode?: 'multiple' | 'single' | 'view'
  value?: any[]
  onConfirm?: (employee: any[]) => void
  onItemClick?: (employee: any) => void
  [key: string]: any
}

const API_MAP: Record<string, (params: any) => Promise<any>> = {
  org: employeeSelectorApi,
  employee: employeeSelectorApi,
  constant: () => Promise.resolve({ data: [] })
}

const MAX_LIMIT = 5

const PersonnelSelectView: React.FC<PersonnelSelectViewProps> = (props) => {
  const { mode = 'single', value, onConfirm, onItemClick } = props

  const { searchParams: _searchParams, selectorParams } =
    findValuesByKeys(props, ['searchParams', 'selectorParams']) || {}
  const searchParams = _searchParams || selectorParams || {}

  const [
    {
      allCount,
      keyword,
      originTreeData,
      treeData,
      currentTreeItem,
      allSelected,
      selectedList,
      selectedOrgList,
      employeeList,
      originEmployeeList,
      employeeLoaded
    },
    setState
  ] = useSetState<any>({
    allCount: 1,
    keyword: '',
    selectedList: [],
    originTreeData: [],
    treeData: [],
    currentTreeItem: [],
    allSelected: false,
    selectedOrgList: [],
    employeeList: [],
    originEmployeeList: [],
    employeeLoaded: false
  })

  // 是否可选择
  const selectable = mode !== 'view'

  // 单选
  const isSingle = mode === SELECT_MODE.SINGLE
  // 人员组件
  // const isEmployeeComponent = type === 'employee'
  // 人员单选
  // const isSingleEmployee = isSingle && isEmployeeComponent

  const onSearchChange = (keyword: string) => {
    setState({
      keyword
    })
  }

  // 递归处理树数据，metadata.count 大于 0 则childs 增加一个直属员工的机构
  const processTreeData = (data: any[]): any[] => {
    if (!isNotEmptyArray(data)) {
      return data
    }
    return data?.map((item: any) => {
      if (item.childs?.length) {
        return {
          ...item,
          childs: [
            ...(item?.metadata?.count > 0
              ? [
                  {
                    id: item?.id,
                    name: '直属员工',
                    // 是否直属
                    is_direct: true,
                    hide: true,
                    metadata: {
                      count: item?.metadata?.count || 0
                    }
                  }
                ]
              : []),
            ...processTreeData(cloneDeep(item.childs))
          ]
        }
      }
      return item
    })
  }

  // 面包屑
  const breadcrumbList = useMemo(() => {
    const breadcrumbs = []
    const findParent = (data: any[], target: any): any[] => {
      for (const item of data) {
        if (item.id === target.id && item?.is_direct === target?.is_direct) {
          return [item]
        }
        if (item.childs) {
          const found = findParent(item.childs, target)
          if (found.length) {
            return [item, ...found]
          }
        }
      }
      return []
    }

    if (originTreeData?.length && currentTreeItem?.id) {
      breadcrumbs.push(...findParent(originTreeData, currentTreeItem))
    }

    return breadcrumbs
  }, [originTreeData, currentTreeItem])

  // 获取机构
  const queryOrgTreeData = async () => {
    const { data } = await employeeSelectorApi({
      selector_code: 'employee',
      with_no_auth: false,
      ...searchParams
    })
    const treeData = processTreeData(
      (data?.item_infos || [])?.find((x: any) => x?.code === 'org')?.item_values || []
    )
    const rootNode = treeData?.[0] || {}
    setState({
      originTreeData: cloneDeep(treeData),
      treeData: cloneDeep(rootNode?.childs || []).filter((x: any) => !x?.hide),
      currentTreeItem: cloneDeep(rootNode || {}),
      allCount: rootNode?.metadata?.all_count || 1
    })
  }

  // 组织点击
  const handleOrgClick = (item: any) => {
    setState({
      currentTreeItem: item,
      treeData: (item?.childs || []).filter((x: any) => !x?.hide)
    })
  }

  // 获取人员
  const queryEmployee = async (params: any) => {
    const { data } = await getemployeeApi({
      sub_flag: false,
      with_no_auth: false,
      paged: false,
      ...params,
      ...searchParams
    })
    return data?.map((x: any) => ({ ...x, item_desc: x?.person_number }))
  }

  // 人员点击
  const handleItemClick = (item: any) => {
    onItemClick?.(item)
  }

  // 人员选择
  const handleEmployeeChange = (val: any, list?: any[]) => {
    setState({ selectedList: list })
  }

  // 组织选择
  const handleOrgChange = (val: any, list?: any[]) => {
    setState({ selectedOrgList: list })
  }

  // 移除
  const onRemove = (item: any, list?: any[]) => {
    setState({ selectedList: list, allSelected: false })
  }

  // 递归获取当前节点的所有子节点id
  const getChildIds = (data: any[]) => {
    return data.reduce((acc, item) => {
      acc.push(item.id)
      if (item.childs) {
        acc.push(...getChildIds(item.childs))
      }
      return acc
    }, [])
  }

  // 选中人员的值
  const tempValue = useMemo(() => {
    if (isSingle) {
      return selectedList?.[0]?.id || null
    }
    return selectedList?.map((x: any) => x?.id)
  }, [selectedList, mode])

  // 将第二个数组中的数据在第一个数组中去除
  const removeData = (data: any[], removeData: any[]) => {
    return data.filter((x) => !removeData.some((y) => y.id === x.id))
  }

  // 根据id获取节点信息
  const getNodeById = (id: string | number, data: any[] = treeData) => {
    const flattenedData = flattenTree(data)
    return flattenedData.find((item: any) => item.id === id)
  }

  // 当前节点是否有子节点
  const hasChild = useMemo(() => {
    return currentTreeItem?.childs?.length > 0
  }, [currentTreeItem])

  // 选中组织的值
  const tempOrgValue = useMemo(() => {
    if (isSingle) {
      return selectedOrgList?.[0]?.id || null
    }
    return selectedOrgList?.map((x: any) => x?.id)
  }, [selectedOrgList, mode])

  // 根据组织获取成员
  useEffect(() => {
    setState({ employeeLoaded: false })
    if (currentTreeItem?.id) {
      queryEmployee({ org_id_list: [currentTreeItem?.id] }).then((list: any) => {
        setState({
          originEmployeeList: cloneDeep(list),
          employeeList: list,
          employeeLoaded: true
        })
      })
    }
  }, [currentTreeItem])

  // 关键字变动
  useUpdateEffect(() => {
    if (keyword) {
      queryEmployee({ keyword }).then((list: any) => {
        setState({ employeeList: list })
      })
    } else {
      setState({ employeeList: originEmployeeList })
    }
  }, [keyword])

  useEffect(() => {
    queryOrgTreeData()
  }, [])

  useEffect(() => {
    setState({ selectedList: value || [] })
  }, [value])

  // 组织选择
  const handleTreeItemChange = async (flag: boolean, treeItem: any) => {
    let tempSelectList = []
    let tempSelectOrgList = []
    // 最后一级或者有搜索条件，直接全选
    if (!hasChild || !!keyword) {
      tempSelectList = [...employeeList]
      tempSelectOrgList = [...selectedOrgList, treeItem]
    } else {
      // 否则去查询所有
      tempSelectList = await queryEmployee({ org_id_list: [treeItem?.id], sub_flag: true })
      tempSelectOrgList = flattenTree([treeItem], 'childs')
    }

    setState({
      selectedOrgList: flag
        ? uniqueItems([...selectedOrgList, ...tempSelectOrgList])
        : removeData(selectedOrgList, tempSelectOrgList),
      selectedList: flag
        ? uniqueItems([...selectedList, ...tempSelectList])
        : removeData(selectedList, tempSelectList)
    })
  }

  // 全选
  const handleAllSelectedChange = (flag: boolean) => {
    handleTreeItemChange(flag, currentTreeItem)
    setState({ allSelected: flag })
  }

  useEffect(() => {
    const isAllOrgSelected = currentTreeItem?.childs?.every((x: any) =>
      selectedOrgList?.map((y: any) => y?.id)?.includes(x?.id)
    )
    const isAllEmployeeSelected = employeeList.every((x: any) =>
      selectedList?.map((y: any) => y?.id)?.includes(x?.id)
    )
    setState({ allSelected: isAllOrgSelected && isAllEmployeeSelected })
  }, [selectedList, employeeList, currentTreeItem, selectedOrgList])

  // 搜索人员是否为空
  const isSearchEmpty = keyword && !employeeList?.length

  // 人员是否显示更多
  const handleEmployeeShowMoreChange = () => {
    const { childs, ...rest } = currentTreeItem || {}
    handleOrgClick({
      ...rest,
      hide: true,
      is_direct: true
    })
  }

  return (
    <HSearchView
      className={classNames('h-tree-search-view', props?.className, {
        'h-tree-search-view-search-empty': isSearchEmpty
      })}
      mode={mode}
      count={allCount}
      selectedList={selectedList}
      onRemove={onRemove}
      allSelected={allSelected}
      onAllSelectedChange={handleAllSelectedChange}
      onConfirm={onConfirm}
      header={
        <>
          {breadcrumbList?.length > 0 && !keyword && (
            <div className='h-tree-select-breadcrumb'>
              {breadcrumbList?.map((item: any, index: any, arr: any) => {
                const isLast = index === arr.length - 1
                return (
                  <Fragment key={item?.name}>
                    <span
                      className={classNames('h-tree-select-breadcrumb-name', {
                        'h-tree-select-breadcrumb-name-last': isLast
                      })}
                      onClick={() => !isLast && handleOrgClick(item)}
                    >
                      {item?.name}
                    </span>
                    {index !== breadcrumbList.length - 1 && (
                      <RightOutline className='h-tree-select-breadcrumb-icon' />
                    )}
                  </Fragment>
                )
              })}
            </div>
          )}
        </>
      }
      onKeywordChange={onSearchChange}
    >
      {!keyword && hasChild && (
        <HTreeNode
          className='h-tree-node-org'
          data={treeData}
          originData={[...selectedOrgList]}
          value={tempOrgValue}
          mode={isSingle ? 'view' : mode}
          keyword={keyword}
          title='下级部门'
          type='employee'
          onItemChange={handleTreeItemChange}
          onItemClick={handleOrgClick}
          onChange={handleOrgChange}
        />
      )}
      <HTreeNode
        className='h-tree-node-employee'
        data={employeeList}
        originData={[...selectedList]}
        mode={mode}
        loaded={employeeLoaded}
        value={tempValue}
        keyword={keyword}
        hasShowMore={hasChild}
        maxLimit={MAX_LIMIT}
        title='成员'
        type='employee'
        showItemDesc
        onShowMoreChange={handleEmployeeShowMoreChange}
        onChange={handleEmployeeChange}
        onItemClick={handleItemClick}
      />
    </HSearchView>
  )
}

export default observer(PersonnelSelectView)
